import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as methods from "../constants/methods";
import * as settings from "../constants/settings";
import * as handlers from "../constants/handlers";
import * as customTabActions from "../actions/customTabActions";
import * as navigationActions from "../actions/navigationActions";
import * as formulaHelper from "../helpers/formulaHelper";
import * as getterHelper from "../helpers/getterHelper";
import * as helper from "../helpers/reducerHelper";
import * as modelHelper from "../helpers/modelHelper";

const customTabInit = (action$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_INIT),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.COLORANT_PANEL_INIT,
                    payload: {
                        tabId: payload.tabId,
                        type: componentTypes.CUSTOM_TAB
                    }
                }
            ];
        })
    );

const cloneMainTabToCustomTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CLONE_MAIN_TO_CUSTOM_TAB),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.mainTabId] === undefined) {
                return [];
            }

            let formulaStore = state$.value.formula[payload.mainTabId];

            if (formulaStore.formula === null) {
                return [];
            }

            let customFormulaTab = helper.getLastTabByType(state$.value.navigation.tabList, componentTypes.CUSTOM_TAB);
            let customFormulaTabId = customFormulaTab !== null ? customFormulaTab.id : payload.tabId;

            let formulaStoreConvertFormula = formulaStore.formula.convertToCustom(
                state$.value.settings.formulaAutomaticNaming,
                state$.value.settings.formulaUnits === settings.UNIT_MANUAL
                    ? settings.UNIT_ML
                    : state$.value.settings.formulaUnits
            );

            let selFandeck = formulaStore.fandeck;
            if (selFandeck && state$.value.global.customFandeckList && !selFandeck.local) {
                let fandeck;
                for (fandeck of state$.value.global.customFandeckList) {
                    if (fandeck.name.toLowerCase() === formulaStore.fandeck.name.toLowerCase()) {
                        selFandeck = fandeck;
                        break;
                    }
                }
            }

            let newTabActions = [
                {
                    type: actionTypes.SAVE_CUSTOM_TAB_DATA,
                    payload: {
                        tabId: customFormulaTabId,
                        product: formulaStore.product,
                        fandeck: selFandeck,
                        color: formulaStore.color,
                        base: formulaStore.base,
                        package: formulaStore.package,
                        packageList: formulaStore.packageList,
                        margin: formulaStore.margin,
                        formula: formulaStoreConvertFormula,
                        activeUnit: formulaStore.activeUnit,
                        spectralData: formulaStore.spectralData,
                        moreInfoText: formulaStore.moreInfoText
                    }
                },
                {
                    type: actionTypes.UPDATE_TAB_COLOR,
                    payload: {
                        tabId: customFormulaTabId,
                        rgb: formulaStore.formula.color.rgb
                    }
                },
                {
                    type: actionTypes.UPDATE_CUSTOM_TAB_NAME,
                    payload: {
                        tabId: customFormulaTabId,
                        name: formulaStoreConvertFormula.color.name
                    }
                }
            ];

            if (formulaStore.product !== null) {
                newTabActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_BASES_BY_PRODUCT,
                        params: {
                            productId: formulaStore.product.id,
                            customFormulaOnly: true
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_BASE_LIST,
                    tabId: customFormulaTabId
                });
                newTabActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_MASTER_PRODUCTS_BY_PRODUCT,
                        params: {
                            productId: formulaStore.product.id
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_MASTER_PRODUCT_LIST,
                    tabId: customFormulaTabId
                });
            }

            newTabActions.push({
                type: actionTypes.RELOAD_FORMULA_META_DATA,
                payload: {
                    tabId: customFormulaTabId
                }
            });

            return newTabActions;
        })
    );

const cloneMatchingTabToCustomTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CLONE_MATCHING_TO_CUSTOM_TAB),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.matchingTabId] === undefined ||
                state$.value.formula[payload.matchingTabId] === undefined ||
                state$.value.formula[payload.matchingTabId].formula === null
            ) {
                return [];
            }

            let customFormulaTab = helper.getLastTabByType(state$.value.navigation.tabList, componentTypes.CUSTOM_TAB);
            let customFormulaTabId = customFormulaTab !== null ? customFormulaTab.id : payload.tabId;

            let newTabActions = [
                {
                    type: actionTypes.CLONE_FORMULA_DATA,
                    payload: {
                        tabId: payload.matchingTabId,
                        newTabId: customFormulaTabId,
                        formula: state$.value.formula[payload.matchingTabId].formula,
                        packageCount: null
                    }
                },
                {
                    type: actionTypes.UPDATE_TAB_COLOR,
                    payload: {
                        tabId: customFormulaTabId,
                        rgb: state$.value.formula[payload.matchingTabId].formula.color.rgb
                    }
                }
            ];

            if (state$.value.matchingTab[payload.matchingTabId].tabScene === componentTypes.DRAFT_FORMULA_SCENE) {
                newTabActions.push(
                    navigationActions.closeActiveTab(
                        payload.matchingTabId,
                        state$.value.navigation.tabList,
                        null,
                        customFormulaTabId
                    )
                );
            }

            if (state$.value.formula[payload.matchingTabId].product !== null) {
                newTabActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_BASES_BY_PRODUCT,
                        params: {
                            productId: state$.value.formula[payload.matchingTabId].product.id,
                            customFormulaOnly: true
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_BASE_LIST,
                    tabId: customFormulaTabId
                });
                newTabActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_MASTER_PRODUCTS_BY_PRODUCT,
                        params: {
                            productId: state$.value.formula[payload.matchingTabId].product.id
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_MASTER_PRODUCT_LIST,
                    tabId: customFormulaTabId
                });

                if (state$.value.formula[payload.matchingTabId].base !== null) {
                    newTabActions.push(
                        customTabActions.getPackagesByParams(
                            customFormulaTabId,
                            state$.value.formula[payload.matchingTabId].product.id,
                            state$.value.formula[payload.matchingTabId].base.id
                        )
                    );
                }
            }

            newTabActions.push({
                type: actionTypes.RELOAD_FORMULA_META_DATA,
                payload: {
                    tabId: customFormulaTabId
                }
            });

            return newTabActions;
        })
    );

const cloneHistoryTabToCustomTab = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CLONE_HISTORY_TO_CUSTOM_TAB),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.statisticsTabId] === undefined) {
                return [];
            }

            let formulaStore = state$.value.formula[payload.statisticsTabId];

            if (formulaStore.formula === null) {
                return [];
            }

            let formula = formulaStore.formula;
            if (payload.statistics.id !== formulaStore.statisticsId) {
                if (payload.statistics.parentId === null) {
                    formula = formula.resetModifications(formulaStore.color);
                } else {
                    formula = formula.editColorantList(
                        modelHelper.getColorantListFromFormula(
                            payload.statistics.cumulativeColorants,
                            state$.value.global.allColorantList,
                            settings.UNIT_ML,
                            state$.value.settings.colorantPanelOrderBy
                        ),
                        settings.UNIT_ML,
                        state$.value.settings.shotsCoef,
                        state$.value.settings.shotsName
                    );
                }
            }

            return [
                {
                    type: actionTypes.SAVE_CUSTOM_TAB_DATA,
                    payload: {
                        tabId: payload.tabId,
                        product: formulaStore.product,
                        fandeck: null,
                        color: formulaStore.color,
                        base: formulaStore.base,
                        package: formulaStore.package,
                        packageList: formulaStore.packageList,
                        margin: formulaStore.margin,
                        formula: formula,
                        activeUnit: getterHelper.getValidActiveUnit(
                            state$.value.settings.lastUsedUnit,
                            state$.value.settings.visibleUnits,
                            componentTypes.CUSTOM_TAB,
                            state$.value.rcc.machine.typeName
                        ),
                        spectralData: formulaStore.spectralData,
                        moreInfoText: formulaStore.moreInfoText
                    }
                },
                {
                    type: actionTypes.UPDATE_TAB_COLOR,
                    payload: {
                        tabId: payload.tabId,
                        rgb: payload.statistics.colorRgb
                    }
                },
                {
                    type: actionTypes.UPDATE_CUSTOM_TAB_NAME,
                    payload: {
                        tabId: payload.tabId,
                        name: payload.statistics.colorName
                    }
                },
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const selectProduct = (action$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_SELECT_PRODUCT),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_BASES_BY_PRODUCT,
                        params: {
                            productId: payload.product.id,
                            customFormulaOnly: true
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_BASE_LIST,
                    tabId: payload.tabId
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_MASTER_PRODUCTS_BY_PRODUCT,
                        params: {
                            productId: payload.product.id
                        }
                    },
                    method: handlers.CUSTOM_TAB_LOAD_MASTER_PRODUCT_LIST,
                    tabId: payload.tabId
                }
            ];
        })
    );

const selectBase = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_SELECT_BASE),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            return [
                customTabActions.getPackagesByParams(payload.tabId, payload.product.id, payload.base.id),
                {
                    type: actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE,
                    payload: {
                        tabId: payload.tabId,
                        imageId: -1
                    }
                }
            ];
        })
    );

const getProductImage = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_SAVE_PACKAGE_LIST, actionTypes.CUSTOM_TAB_SET_PACKAGE),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined) {
                return [];
            }

            if (payload.selectedPackage === null || payload.selectedPackage.package.isCustom()) {
                return [
                    {
                        type: actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE,
                        payload: {
                            tabId: payload.tabId,
                            imageId: -1
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base.name,
                            packageName: payload.selectedPackage.package.name,
                            online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: payload.tabId
                }
            ];
        })
    );

const selectCustomFandeck = (action$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_SELECT_FANDECK),
        switchMap(() => {
            return [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.CUSTOM_FANDECKS_MODAL
                    }
                }
            ];
        })
    );

const checkSelectedFandeck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_CUSTOM_FANDECK_LIST),
        switchMap(({payload}) => {
            let customTabs = state$.value.navigation.tabList.filter((tab) => tab.type === componentTypes.CUSTOM_TAB);
            let tab,
                requests = [];

            for (tab of customTabs) {
                if (state$.value.formula[tab.id] && state$.value.formula[tab.id].fandeck) {
                    if (!state$.value.formula[tab.id].fandeck.local && state$.value.global.customFandeckList) {
                        let selFandeckName = state$.value.formula[tab.id].fandeck.name.toLowerCase();
                        let fandeck;
                        for (fandeck of state$.value.global.customFandeckList) {
                            if (fandeck.name.toLowerCase() === selFandeckName) {
                                requests.push({
                                    type: actionTypes.CUSTOM_TAB_AUTOSELECT_FANDECK,
                                    payload: {
                                        tabId: tab.id,
                                        fandeck: fandeck
                                    }
                                });
                                break;
                            }
                        }
                    }

                    if (
                        !getterHelper.getObjectById(
                            payload.customFandeckList,
                            state$.value.formula[tab.id].fandeck.id
                        ) &&
                        state$.value.formula[tab.id].fandeck.local
                    ) {
                        requests.push({
                            type: actionTypes.CUSTOM_TAB_REMOVE_SELECTED_FANDECK,
                            payload: {
                                tabId: tab.id
                            }
                        });
                    }
                }
            }

            return requests;
        })
    );

const updateTabHeaderColor = (action$) =>
    action$.pipe(
        ofType(actionTypes.FORMULA_TAB_EDIT_FORMULA_RGB),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.UPDATE_TAB_COLOR,
                    payload: {
                        tabId: payload.tabId,
                        rgb: payload.rgb
                    }
                }
            ];
        })
    );

const updateTabHeaderName = (action$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_CHANGE_FORMULA_NAME),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.UPDATE_CUSTOM_TAB_NAME,
                    payload: {
                        tabId: payload.tabId,
                        name: payload.name
                    }
                }
            ];
        })
    );

const addCustomFormula = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CUSTOM_TAB_ADD_CUSTOM_FORMULA),
        switchMap(({payload}) => {
            if (
                state$.value.customTab[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId] === undefined
            ) {
                return [];
            }

            const formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.base === null ||
                formulaStore.package === null ||
                formulaStore.formula === null ||
                formulaStore.formula.color.name === null ||
                formulaStore.formula.color.name.trim().length === 0 ||
                formulaStore.formula.realColorantList.length === 0 ||
                formulaStore.validation?.hasColorantBaseRulesError()
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_ADD_CUSTOM_FORMULA,
                        params: {
                            productId: formulaStore.product.id,
                            fandeckId: formulaStore.fandeck.id,
                            colorName: formulaStore.formula.color.name,
                            colorCode: state$.value.settings.formulaCode ? formulaStore.formula.color.code : null,
                            rgb: formulaStore.formula.color.rgb,
                            formulaSourceId: formulaHelper.getOwnFormulaSource(
                                state$.value.customTab[payload.tabId].matchingFormula
                            ),
                            moreInfo: formulaStore.moreInfoText ?? "",
                            colorants: formulaStore.formula.getRealColorantAmounts(),
                            baseId: formulaStore.base.id,
                            baseInProductPackageId: formulaStore.package.package.isCustom()
                                ? null
                                : formulaStore.package.id,
                            customPackage: formulaStore.package.package.isCustom(),
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnitId: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            formulaForMasterProduct: state$.value.customTab[payload.tabId].masterProduct,
                            reflData: formulaStore.spectralData?.spectral ?? null,
                            labl: formulaStore.spectralData?.isLabValid() ? formulaStore.spectralData.lab[0] : null,
                            laba: formulaStore.spectralData?.isLabValid() ? formulaStore.spectralData.lab[1] : null,
                            labb: formulaStore.spectralData?.isLabValid() ? formulaStore.spectralData.lab[2] : null
                        }
                    },
                    method: handlers.ADD_CUSTOM_FORMULA,
                    tabId: payload.tabId
                }
            ];
        })
    );

export default [
    customTabInit,
    cloneMainTabToCustomTab,
    cloneMatchingTabToCustomTab,
    cloneHistoryTabToCustomTab,
    selectProduct,
    selectBase,
    getProductImage,
    selectCustomFandeck,
    checkSelectedFandeck,
    updateTabHeaderColor,
    updateTabHeaderName,
    addCustomFormula
];
